import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { sheet } from '../styles.js';

export class Toast extends LitElement {
	static styles = [sheet];

	static properties = {
		toastId: { type: String, reflect: true },
		type: { type: String },
		title: { type: String },
		message: { type: String },
		dismissible: { type: Boolean },
		duration: { type: Number },
		// Named handlers for action elements in the (raw HTML) title/message: an element carrying
		// click="<name>" invokes callbacks[name]({ event, trigger }) when clicked.
		callbacks: { attribute: false }
	};

	#bsToast = null;
	#hideTimeout = null;
	#abortController = null;

	constructor() {
		super();
		this.type = 'info';
		this.title = '';
		this.message = '';
		this.dismissible = true;
		this.duration = 5000;
		this.callbacks = {};
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#clearAutoHide();
		// Abort all event listeners
		this.#abortController?.abort();
		this.#bsToast?.dispose();
	}

	firstUpdated() {
		this.#initBootstrapToast();
	}

	updateOptions(options = {}) {
		const durationChanged = options.duration !== undefined && options.duration !== this.duration;
		
		// Update properties
		if (options.type !== undefined) this.type = options.type;
		if (options.title !== undefined) this.title = options.title;
		if (options.message !== undefined) this.message = options.message;
		if (options.dismissible !== undefined) this.dismissible = options.dismissible;
		if (options.duration !== undefined) this.duration = options.duration;
		
		// Only restart auto-hide if duration changed
		if (durationChanged) {
			this.#clearAutoHide();
			if (this.duration > 0) {
				this.#startAutoHide();
			}
		}
		this.requestUpdate();
	}

	hide() {
		this.#bsToast?.hide();
	}

	render() {
		return html`
			<div class="toast ${classMap({ [this.#getBackgroundClass()]: true })} border-0 position-relative overflow-hidden" @click=${this.#onActionClick}>
				<div class="d-flex">
					<div class="toast-body w-100">
						<strong>${unsafeHTML(this.title)}</strong>
						${unsafeHTML(this.message)}
					</div>
					${this.dismissible ? html`<button type="button" class="btn-close ${this.type === 'neutral' ? '' : 'fg-white'} me-3 m-auto" @click=${() => { this.hide(); }}></button>` : ''}
				</div>
			</div>
		`;
	}

	// Delegated so it works with the raw-HTML message and survives re-renders. Clicking an element
	// with a click="<name>" attribute runs the matching entry from `callbacks`, passed { event, trigger }.
	#onActionClick = (event) => {
		const trigger = event.target?.closest?.('[click]');
		if (!trigger) {
			return;
		}
		
		const handler = this.callbacks?.[trigger.getAttribute('click')];
		if (typeof handler !== 'function') {
			return;
		}

		event.preventDefault();
		handler({ event, trigger });
	};

	#initBootstrapToast(retryCount = 0) {
		const toastEl = this.renderRoot.querySelector('.toast');
		if (!toastEl) {
			// Element not ready, try again (max 5 retries)
			if (retryCount < 5) {
				setTimeout(() => this.#initBootstrapToast(retryCount + 1), 10);
			}
			return;
		}
		
		// Abort previous event listeners
		this.#abortController?.abort();
		this.#abortController = new AbortController();
		const { signal } = this.#abortController;
		
		// Remove Bootstrap's show class to ensure fresh state
		toastEl.classList.remove('show');
		
		// Get existing Bootstrap Toast instance if any and dispose it
		const existingInstance = bootstrap.Toast.getInstance(toastEl);
		if (existingInstance) {
			existingInstance.dispose();
		}
		
		// Create Bootstrap Toast WITHOUT autohide - we'll handle it manually
		this.#bsToast = new bootstrap.Toast(toastEl, { autohide: false });
		
		// Attach event listeners with abort signal
		toastEl.addEventListener('hidden.bs.toast', this.#onHidden, { signal });
		toastEl.addEventListener('mouseenter', this.#onMouseEnter, { signal });
		toastEl.addEventListener('mouseleave', this.#onMouseLeave, { signal });
		
		// Show the toast
		this.#bsToast.show();
		
		// Start our own timer if has duration
		if (this.duration > 0) {
			this.#startAutoHide();
		}
	}

	#getBackgroundClass() {
		const backgrounds = {
			success: 'bd-green-500',
			error: 'bd-red-500',
			warning: 'bd-orange-500',
			info: 'bd-blue-500',
			neutral: 'bd-gray-100',
		};
		return backgrounds[this.type] || 'bd-blue-500';
	}

	#startAutoHide() {
		this.#clearAutoHide();
		// Set hide timeout
		this.#hideTimeout = setTimeout(() => {
			this.#bsToast?.hide();
		}, this.duration);
	}
		
	#clearAutoHide() {
		if (this.#hideTimeout) {
			clearTimeout(this.#hideTimeout);
			this.#hideTimeout = null;
		}
	}

	// Arrow functions for event handlers (auto-bound)
	#onHidden = () => {
		this.dispatchEvent(new CustomEvent('toast-hidden', {
			detail: { id: this.toastId },
			bubbles: true,
			composed: true
		}));
	};
		
	#onMouseEnter = () => {
		this.#clearAutoHide();
	};
		
	#onMouseLeave = () => {
		if (this.duration > 0) {
			this.#startAutoHide();
		}
	};
}

customElements.define('u-toast', Toast);